import { RefObject, useEffect } from 'react'
import { VListHandle } from 'virtua'
import { DisplayUeopt, GridLocation } from '../../constants/enums'
import { usePrevious } from './usePrevious'

const PRESERVE_PAGE_STATE_DURATION = 10000

type PreservePathStateEntry = {
    positionTop: number
    timeout: ReturnType<typeof setTimeout>
}
export type PreservePathState = {
    [gridLocation: string /*GridLocation*/]: PreservePathStateEntry
}

// const getRandomNumber = () => 2 // account for something??

interface UsePreservePageStateParams {
    /** A GridLocation enum value for identity, omit to disable */
    gridLocation?: GridLocation
    /** Ref to virtua VList */
    listRef?: RefObject<VListHandle>
    refScrollTop: RefObject<number>
    displayMode: DisplayUeopt
    gridDensity?: number
    exploreTab?: string
    searchQuery?: string
    disablePreserveScrollPosition?: boolean
}

/**
 * Preserve/restore the scroll position of a virtualized list on page navigation
 */
export const usePreservePageState = (params: UsePreservePageStateParams) => {
    const {
        gridLocation,
        listRef,
        refScrollTop,
        displayMode,
        gridDensity,
        exploreTab,
        searchQuery,
        disablePreserveScrollPosition,
    } = params

    const previousDisplayMode = usePrevious(displayMode)
    const previousGridDensity = usePrevious(gridDensity)

    useEffect(() => {
        if (!gridLocation) return

        clearTimeout(window.__PRESERVE_PAGE_STATE__[gridLocation]?.timeout)

        if (disablePreserveScrollPosition) {
            delete window.__PRESERVE_PAGE_STATE__[gridLocation]
            return
        }

        return () => {
            clearTimeout(window.__PRESERVE_PAGE_STATE__[gridLocation]?.timeout)
            window.__PRESERVE_PAGE_STATE__[gridLocation] = {
                positionTop: refScrollTop.current,
                // only retain the scroll position for `PRESERVE_PAGE_STATE_DURATION` milliseconds
                timeout: setTimeout(() => {
                    if (typeof window === 'undefined') return
                    if (!window.__PRESERVE_PAGE_STATE__[gridLocation]) return
                    const { timeout } =
                        window.__PRESERVE_PAGE_STATE__[gridLocation]
                    clearTimeout(timeout)

                    delete window.__PRESERVE_PAGE_STATE__[gridLocation]
                }, PRESERVE_PAGE_STATE_DURATION),
            }
        }
    }, [
        gridLocation,
        exploreTab,
        searchQuery,
        refScrollTop,
        disablePreserveScrollPosition,
    ])

    useEffect(() => {
        if (!gridLocation) return

        if (
            !previousDisplayMode &&
            !previousGridDensity &&
            previousDisplayMode === displayMode &&
            previousGridDensity === gridDensity
        ) {
            return
        }

        if (listRef?.current) {
            // scroll to top of page
            listRef.current.scrollTo(0)
        }
    }, [displayMode, gridDensity])

    return !disablePreserveScrollPosition
        ? window.__PRESERVE_PAGE_STATE__[gridLocation]
        : ({} as PreservePathStateEntry)
}
